// @flow
import React, { Component } from 'react';
import { withHelmet } from 'infrastructure/seo/Helmet';

import { STATUS_UNKNOWN, STATUS_LOADING, STATUS_DEFAULT } from 'constants/statusConstants';
import { getMetaBySlug } from 'utils/seoUtils';

import PageNotFound from 'components/app/PageNotFound';
import Loading from 'components/fragments/Loading';
import PageHubDefault from 'components/page/template/PageHubDefault';
import PageListNode from 'components/page/template/PageListNode';
import PagePartners from 'containers/page/PagePartnersContainers';
import PageMedia from 'components/page/template/PageMedia';

import type { Status } from 'types/Status';
import type { CardType, NewsNunType } from 'types/PageList';
import type { BlocContent } from 'types/NewsletterBloc';
import type { ArticleType } from 'types/Article';
import type { AlbumType } from 'types/Albums';

export type StateProps = {
  template: string,
  title: string,
  description: BlocContent[],
  excerpt: string,
  meta_title: string,
  meta_description: string,
  label: {
    id: number,
    name: string,
    slug: string
  },
  featured_media: {
    src: string,
    legend: string
  },
  liste_enfants: CardType[],
  liste_cards: CardType[],
  articles: ArticleType[],
  news: NewsNunType[],
  redirection: string,
  status: Status,
  banniere: string,
  placeholder: string,
  sidebar: BlocContent[],
  albums: AlbumType[],
  albumsStatus: Status,
  organization: string,
  rencontresLive: number,
  albums_filtres: {
    term_id: number,
    name: string,
    slug: string
  }[],
  albums_saisons: {
    term_id: number,
    name: string,
    slug: string
  }[]
};

export type DispatchProps = {
  fetchPageList: (slug: string) => void,
  fetchAlbums: () => void
};

type Props = {
  match: {
    params: {
      slug: string
    }
  },
  pageSlug: string
} & DispatchProps &
  StateProps;

class PageList extends Component<Props> {
  componentDidMount() {
    const { fetchPageList, pageSlug } = this.props;
    const { slug } = this.props.match.params;
    if (slug) {
      fetchPageList(slug);
    } else {
      fetchPageList(pageSlug);
    }
  }

  componentDidUpdate(prevState) {
    const { template, fetchAlbums, albumsStatus } = this.props;
    const { template: prevTemplate } = prevState;
    if (
      (prevTemplate !== template && template === 'mediatheque') ||
      (albumsStatus === STATUS_DEFAULT && template === 'mediatheque')
    ) {
      fetchAlbums();
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    const { slug } = this.props.match.params;
    const { fetchPageList } = this.props;
    const { slug: nextSlug } = nextProps.match.params;

    if (nextProps !== this.props) {
      if (nextSlug !== slug) {
        if (nextSlug) {
          fetchPageList(nextSlug);
          return true;
        }
      }
      return true;
    }
    return false;
  }

  render() {
    const { props } = this;
    const { status, template, liste_enfants } = props;
    const enfants = liste_enfants.filter(enfant => enfant.title !== '');

    if (status === STATUS_LOADING) {
      return <Loading />;
    }

    if (status === STATUS_UNKNOWN) {
      return <PageNotFound noMargin />;
    }

    switch (template) {
      case 'mediatheque':
        return <PageMedia {...props} />;
      case 'partenaires':
        return <PagePartners {...props} />;
      default:
        return enfants.length > 0 ? <PageHubDefault {...props} /> : <PageListNode {...props} />;
    }
  }
}

export default withHelmet(PageList)((Helmet, pageProps) => {
  const {
    match: {
      params: { slug }
    },
    banniere,
    meta_description,
    meta_title,
    organization,
    placeholder,
    ville
  } = pageProps;

  const meta = getMetaBySlug(slug);
  const ogimage = pageProps.featured_media.src ? pageProps.featured_media.src : banniere || placeholder;
  const ogimagetitle = pageProps.featured_media.title;

  return (
    <Helmet>
      <title>{meta_title}</title>
      <meta name={'description'} content={meta_description} />
      <meta name={'keywords'} content={meta.keywords(ville, organization)} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={meta_title} />
      <meta property={'og:description'} content={meta_description} />

      {ogimage && <meta property={'og:image'} content={ogimage} />}
      {ogimage && <meta property={'og:image:url'} content={ogimage} />}
      {ogimage && ogimagetitle && <meta property={'og:image:title'} content={ogimagetitle} />}
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
